import { createSlice } from '@reduxjs/toolkit';
import { SLACK_MESSAGES_STATUS, SLACK_SEND_STATUS, isSlackRetryStatus } from 'utils/slackMessages';

const initialState = {
    // transactionId -> { status, items }
    messagesByTransactionId: {},
    // transactionId -> true while the Slack dialog is open
    dialogOpenByTransactionId: {},
    // transactionId -> latest send from the dialog, kept after FAILED / UNKNOWN so reopening the dialog offers Retry
    // of the same request: { status, slackMessageLocationId, text, generatedText, detail, panelClosed }
    // panelClosed marks a send whose panel was closed while it was running (cleared if the panel opens again).
    sendByTransactionId: {},
};

const isSending = (entry) => entry?.status === SLACK_SEND_STATUS.SENDING;

// A send still running when its panel closes (e.g. after Submit) is kept so its result is still shown; it is dropped
// once that result arrives (see sendFinished).
const removeSendUnlessRunning = (state, transactionId) => {
    const entry = state.sendByTransactionId[transactionId];
    if (isSending(entry)) entry.panelClosed = true;
    else delete state.sendByTransactionId[transactionId];
};

const slackSlice = createSlice({
    name: 'slack',
    initialState,
    reducers: {
        fetchMessagesStart(state, { payload: { transactionId } }) {
            const entry = state.messagesByTransactionId[transactionId];
            state.messagesByTransactionId[transactionId] = { status: SLACK_MESSAGES_STATUS.LOADING, items: entry?.items || [] };
        },
        fetchMessagesSuccess(state, { payload: { transactionId, items } }) {
            state.messagesByTransactionId[transactionId] = { status: SLACK_MESSAGES_STATUS.LOADED, items: items || [] };
        },
        fetchMessagesFailed(state, { payload: { transactionId } }) {
            const entry = state.messagesByTransactionId[transactionId];
            state.messagesByTransactionId[transactionId] = { status: SLACK_MESSAGES_STATUS.ERROR, items: entry?.items || [] };
        },

        setDialogOpen(state, { payload: { transactionId, open = true } }) {
            if (open) {
                state.dialogOpenByTransactionId[transactionId] = true;
                return;
            }
            // A FAILED / UNKNOWN send is kept: closing the dialog must not lose the request that reopening offers to Retry.
            delete state.dialogOpenByTransactionId[transactionId];
        },

        // payload: { transactionId, subject, channelName, generatedText, request: { slackMessageLocationId, text, edited } }
        // (subject / channelName are read by the saga for the snackbars.) The dialog closes on Send; the progress and the
        // result are shown as snackbars. text / generatedText let Retry restore exactly this request.
        sendStart(state, { payload: { transactionId, generatedText = '', request } }) {
            // The saga ignores a second send while one is running (takeLeadingPerKey), so the state must keep the
            // request actually in flight - otherwise Retry after a failure would restore the ignored one.
            if (isSending(state.sendByTransactionId[transactionId])) return;
            state.sendByTransactionId[transactionId] = {
                status: SLACK_SEND_STATUS.SENDING,
                slackMessageLocationId: request.slackMessageLocationId,
                text: request.text,
                generatedText,
                detail: null,
                panelClosed: false,
            };
            delete state.dialogOpenByTransactionId[transactionId];
        },
        // payload: { transactionId, logId, status, detail } from a .SlackSendResultMessage. No state change: the send
        // saga buffers these from before its POST and matches them by logId; the late-result saga takes the rest.
        sendResultReceived() {},
        sendFinished(state, { payload: { transactionId, status, detail = null } }) {
            const entry = state.sendByTransactionId[transactionId];
            // Only FAILED / UNKNOWN are kept (for Retry); after SUCCESS or a 400 rejection the next send starts fresh.
            // A send whose panel is already closed is not kept either: its result snackbar is all the Agent needs.
            if (!isSlackRetryStatus(status) || entry?.panelClosed) {
                delete state.sendByTransactionId[transactionId];
                delete state.dialogOpenByTransactionId[transactionId];
                return;
            }
            if (!entry) return;
            entry.status = status;
            entry.detail = detail;
        },
        // The real result of a send that already ended as UNKNOWN on the FE (POST or websocket result timed out).
        // A send running meanwhile (Retry) owns the entry and is left alone.
        lateResultReceived(state, { payload: { transactionId, status, detail = null } }) {
            const entry = state.sendByTransactionId[transactionId];
            if (!entry || isSending(entry)) return;
            if (status === SLACK_SEND_STATUS.SUCCESS) {
                // Delivered after all: a Retry would only be rejected as "already sent".
                delete state.sendByTransactionId[transactionId];
                delete state.dialogOpenByTransactionId[transactionId];
                return;
            }
            if (status === SLACK_SEND_STATUS.FAILED) {
                entry.status = status;
                entry.detail = detail;
            }
        },
        clearSendState(state, { payload: { transactionId } }) {
            if (isSending(state.sendByTransactionId[transactionId])) return;
            delete state.sendByTransactionId[transactionId];
        },

        // Closing panels (e.g. after Submit) keeps a send that is still running: its result arrives later and is still
        // shown, and until then a reopened panel keeps Slack disabled (a second send would be ignored by the saga).
        removeTransaction(state, { payload: transactionId }) {
            delete state.messagesByTransactionId[transactionId];
            delete state.dialogOpenByTransactionId[transactionId];
            removeSendUnlessRunning(state, transactionId);
        },
        resetAll(state) {
            state.messagesByTransactionId = {};
            state.dialogOpenByTransactionId = {};
            Object.keys(state.sendByTransactionId).forEach((transactionId) => removeSendUnlessRunning(state, transactionId));
        },
        // The panel of a send closed while running is open again: a FAILED / UNKNOWN result is kept for Retry again.
        panelReopened(state, { payload: transactionId }) {
            const entry = state.sendByTransactionId[transactionId];
            if (entry) entry.panelClosed = false;
        },
    },
});

export const slackReducer = slackSlice.reducer;
export const slackActions = slackSlice.actions;
